import {
  FETCH_ZONIFICACION,
  FETCH_ZONIFICACION_SUCCESS,
  FETCH_ZONIFICACION_FAILED,
  EDIT_ZONIFICACION,
  FETCH_ZONIFICACION_CODES_SUCCESS,
} from '../store/types';
import { firebase } from '../config/configureFirebase';
import { onValue, set, remove, push, update, get, ref } from 'firebase/database';
import { normalizeSignupCode } from '../other/networkSlots';

export const SLOTS_TOTAL = 100;

function randomChunk(len = 6) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < len; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

function cityPrefix(name) {
  const clean = String(name || 'CITY')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase();
  return (clean.slice(0, 3) || 'CTY').padEnd(3, 'X');
}

export function generateMasterCode(cityName) {
  return `${cityPrefix(cityName)}-MAESTRO-${randomChunk(4)}`;
}

export function generateIndividualCodes(cityName, count = SLOTS_TOTAL) {
  const prefix = cityPrefix(cityName);
  const codes = new Set();
  while (codes.size < count) {
    codes.add(`${prefix}-${randomChunk(6)}`);
  }
  return Array.from(codes);
}

export const fetchZonificacionCities = () => (dispatch) => {
  const { zonificacionCitiesRef } = firebase;
  dispatch({ type: FETCH_ZONIFICACION, payload: null });
  onValue(
    zonificacionCitiesRef,
    (snapshot) => {
      if (snapshot.val()) {
        const data = snapshot.val();
        const arr = Object.keys(data).map((i) => {
          const row = { ...data[i], id: i };
          row.slotsTotal = row.slotsTotal || SLOTS_TOTAL;
          row.slotsUsed = row.slotsUsed || 0;
          row.slotsFull = row.slotsUsed >= row.slotsTotal;
          return row;
        });
        dispatch({ type: FETCH_ZONIFICACION_SUCCESS, payload: arr });
      } else {
        dispatch({ type: FETCH_ZONIFICACION_SUCCESS, payload: [] });
      }
    },
    (err) => {
      dispatch({
        type: FETCH_ZONIFICACION_FAILED,
        payload: err?.message || 'zonificacion_fetch_failed',
      });
    }
  );
};

export const fetchCityCodes = (cityId) => async (dispatch) => {
  const { zonificacionCityCodesRef } = firebase;
  const snap = await get(zonificacionCityCodesRef(cityId));
  const val = snap.val() || {};
  const codes = Object.keys(val).map((code) => ({ code, ...val[code] }));
  dispatch({
    type: FETCH_ZONIFICACION_CODES_SUCCESS,
    payload: { cityId, codes },
  });
  return codes;
};

async function writeCodeMaps(db, cityId, entries) {
  const rootUpdates = {};
  Object.keys(entries).forEach((code) => {
    rootUpdates[`zonificacion/colombia/codes/${code}`] = entries[code];
    rootUpdates[`zonificacion/colombia/cityCodes/${cityId}/${code}`] = entries[code];
  });
  await update(ref(db, '/'), rootUpdates);
}

export const editZonificacionCity = (city, method) => async (dispatch) => {
  const {
    database: db,
    zonificacionCitiesRef,
    zonificacionCityEditRef,
    zonificacionCityCodesRef,
  } = firebase;

  dispatch({ type: EDIT_ZONIFICACION, payload: { method, city } });

  if (method === 'Add') {
    const masterCode = (city.masterCode || generateMasterCode(city.name)).toUpperCase();
    const payload = {
      name: city.name,
      department: city.department || '',
      active: city.active !== false,
      country: 'CO',
      slotsTotal: SLOTS_TOTAL,
      slotsUsed: 0,
      slotsFull: false,
      masterCode,
      carTypes: city.carTypes || {},
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const newRef = await push(zonificacionCitiesRef, payload);
    const cityId = newRef.key;

    const individuals = generateIndividualCodes(city.name, SLOTS_TOTAL);
    const entries = {};
    individuals.forEach((code) => {
      entries[code] = {
        cityId,
        type: 'individual',
        used: false,
        usedBy: null,
        usedAt: null,
        active: true,
      };
    });
    entries[masterCode] = {
      cityId,
      type: 'master',
      used: false,
      usedBy: null,
      usedAt: null,
      active: true,
    };
    await writeCodeMaps(db, cityId, entries);
    return cityId;
  }

  if (method === 'Delete') {
    const codesSnap = await get(zonificacionCityCodesRef(city.id));
    const codes = codesSnap.val() || {};
    const removals = {};
    Object.keys(codes).forEach((code) => {
      removals[`zonificacion/colombia/codes/${code}`] = null;
    });
    removals[`zonificacion/colombia/cityCodes/${city.id}`] = null;
    removals[`zonificacion/colombia/cities/${city.id}`] = null;
    await update(ref(db, '/'), removals);
    return;
  }

  if (method === 'Update') {
    const { id, ...rest } = city;
    const data = {
      ...rest,
      updatedAt: Date.now(),
      slotsFull: (rest.slotsUsed || 0) >= (rest.slotsTotal || SLOTS_TOTAL),
    };
    await set(zonificacionCityEditRef(id), data);
    return;
  }

  if (method === 'GenerateCodes') {
    const cityId = city.id;
    const citySnap = await get(zonificacionCityEditRef(cityId));
    const cityData = citySnap.val();
    if (!cityData) return [];

    const existingSnap = await get(zonificacionCityCodesRef(cityId));
    const existing = existingSnap.val() || {};
    const clean = {};
    Object.keys(existing).forEach((code) => {
      if (existing[code].type === 'individual' && !existing[code].used) {
        clean[`zonificacion/colombia/codes/${code}`] = null;
        clean[`zonificacion/colombia/cityCodes/${cityId}/${code}`] = null;
      }
    });
    if (Object.keys(clean).length) {
      await update(ref(db, '/'), clean);
    }

    const remaining = Math.max(
      0,
      (cityData.slotsTotal || SLOTS_TOTAL) - (cityData.slotsUsed || 0)
    );
    const fresh = generateIndividualCodes(cityData.name, remaining);
    const entries = {};
    fresh.forEach((code) => {
      entries[code] = {
        cityId,
        type: 'individual',
        used: false,
        usedBy: null,
        usedAt: null,
        active: true,
      };
    });
    if (Object.keys(entries).length) {
      await writeCodeMaps(db, cityId, entries);
    }
    return fresh;
  }
};

export const validateCityCodeClient = async (code, cityId) => {
  const { config } = firebase;
  const url = `https://${config.projectId}.web.app/validate_city_code`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: normalizeSignupCode(code), cityId }),
  });
  return res.json();
};

/**
 * Alinea slotsUsed/slotsFull de una ciudad con el conteo real de líderes (1ºs 100).
 * Evita barra en 100/100 cuando solo hay p.ej. 98 líderes y bloquea registro a destiempo.
 */
export const reconcileCityLeaderSlots = (cityId, leadersCount) => async () => {
  const { zonificacionCityEditRef } = firebase;
  if (!cityId) return { ok: false, reason: 'missing_city' };
  const snap = await get(zonificacionCityEditRef(cityId));
  const city = snap.val();
  if (!city) return { ok: false, reason: 'not_found' };
  const total = Number(city.slotsTotal) > 0 ? Number(city.slotsTotal) : SLOTS_TOTAL;
  const used = Math.max(0, Math.min(total, Math.floor(Number(leadersCount) || 0)));
  const prev = Number(city.slotsUsed) || 0;
  const prevFull = !!city.slotsFull;
  const nextFull = used >= total;
  if (prev === used && prevFull === nextFull) {
    return { ok: true, changed: false, slotsUsed: used, slotsTotal: total };
  }
  await update(zonificacionCityEditRef(cityId), {
    slotsUsed: used,
    slotsFull: nextFull,
    updatedAt: Date.now(),
  });
  return { ok: true, changed: true, slotsUsed: used, slotsTotal: total, prev };
};

const RATE_OVERRIDE_KEYS = [
  'base_fare',
  'min_fare',
  'min_fare_distance',
  'rate_per_unit_distance',
  'rate_per_hour',
  'convenience_fees',
  'convenience_fee_type',
  'fleet_admin_fee',
];

/** Copia tarifas reales del Car Type global (punto de partida por ciudad). */
export function seedCityRatesFromGlobal(car) {
  if (!car) return {};
  return {
    base_fare: numOrNull(car.base_fare),
    min_fare: numOrNull(car.min_fare),
    min_fare_distance: numOrNull(car.min_fare_distance),
    rate_per_unit_distance: numOrNull(car.rate_per_unit_distance),
    rate_per_hour: numOrNull(car.rate_per_hour),
    convenience_fees: numOrNull(car.convenience_fees),
    convenience_fee_type: car.convenience_fee_type || 'percentage',
    fleet_admin_fee: numOrNull(car.fleet_admin_fee),
  };
}

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Mergea tarifas de ciudad sobre un car type global. */
export function mergeCarTypeWithCity(carType, cityCarTypes) {
  if (!carType) return carType;
  if (!cityCarTypes || !cityCarTypes[carType.id] || !cityCarTypes[carType.id].enabled) {
    return carType;
  }
  const ov = cityCarTypes[carType.id];
  const hasOwnRates = RATE_OVERRIDE_KEYS.some((k) => ov[k] != null && ov[k] !== '');
  // followGlobal true (o default sin tarifas propias) → siempre Car Type global vigente.
  if (ov.followGlobal === true || (ov.followGlobal !== false && !hasOwnRates)) {
    return carType;
  }
  const merged = { ...carType };
  RATE_OVERRIDE_KEYS.forEach((k) => {
    if (ov[k] != null && ov[k] !== '') {
      merged[k] = ov[k];
    }
  });
  return merged;
}

/** Orden de lista = pos de Tipos de vehículo (luego nombre). */
export function sortCarsByPos(cars) {
  return [...(cars || [])].sort((a, b) => {
    const pa = Number(a?.pos);
    const pb = Number(b?.pos);
    const na = Number.isFinite(pa) ? pa : 9999;
    const nb = Number.isFinite(pb) ? pb : 9999;
    if (na !== nb) return na - nb;
    return String(a?.name || '').localeCompare(String(b?.name || ''), 'es');
  });
}

/**
 * Filtra y aplica tarifas de ciudad.
 * Si la ciudad no define carTypes, devuelve la lista global intacta.
 * Siempre ordena por `pos` (misma posición que Tipos de vehículo).
 */
export function resolveCarsForCity(cars, city) {
  if (!cars || !cars.length) return cars || [];
  if (!city || !city.carTypes || !Object.keys(city.carTypes).length) {
    return sortCarsByPos(cars);
  }
  return sortCarsByPos(
    cars
      .filter((c) => city.carTypes[c.id]?.enabled)
      .map((c) => mergeCarTypeWithCity(c, city.carTypes))
  );
}

export function findCityById(cities, cityId) {
  if (!cityId || !cities || !cities.length) return null;
  return cities.find((c) => c.id === cityId) || null;
}

/** Ciudades activas con al menos un Car Type habilitado (tienen tarifas locales). */
export function getPricingCities(cities) {
  return (cities || []).filter(
    (c) =>
      c.active !== false &&
      c.carTypes &&
      Object.keys(c.carTypes).some((id) => c.carTypes[id]?.enabled)
  );
}

/** Normaliza nombre de ciudad para comparar (sin tildes, minúsculas). */
export function normalizeCityName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Alias de municipios / zonas → ciudad con tarifas.
 * Evita que Itagüí/Envigado/etc. fallen frente a "Medellín" en reverse geocode.
 * También acepta `city.aliases` (array o string CSV) desde RTDB.
 */
const CITY_HINT_ALIASES = {
  medellin: [
    'itagui',
    'envigado',
    'bello',
    'sabaneta',
    'la estrella',
    'copacabana',
    'girardota',
    'barbosa',
    'caldas',
    'valle de aburra',
    'area metropolitana',
    'abejorral',
  ],
  barranquilla: [
    'soledad',
    'malambo',
    'galapa',
    'puerto colombia',
    'baranoa',
  ],
};

function cityAliasList(city) {
  const fromDb = Array.isArray(city?.aliases)
    ? city.aliases
    : String(city?.aliases || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  const key = normalizeCityName(city?.name);
  const builtin = CITY_HINT_ALIASES[key] || [];
  return [...builtin, ...fromDb].map(normalizeCityName).filter(Boolean);
}

function hintMatchesCity(hintNorm, city) {
  if (!hintNorm || !city) return false;
  const cn = normalizeCityName(city.name);
  if (!cn) return false;
  if (cn === hintNorm) return true;
  if (cn.length >= 4 && (hintNorm.includes(cn) || cn.includes(hintNorm))) return true;
  return cityAliasList(city).some((alias) => {
    if (!alias || alias.length < 3) return false;
    return hintNorm === alias || hintNorm.includes(alias) || alias.includes(hintNorm);
  });
}

/**
 * Busca ciudad con tarifas cuyo nombre (o alias metro) coincida con un hint (GPS, dirección, etc.).
 */
export function matchPricingCityByHint(cities, hint) {
  const pricing = getPricingCities(cities);
  const n = normalizeCityName(hint);
  if (!n || !pricing.length) return null;
  const exact = pricing.find((c) => normalizeCityName(c.name) === n);
  if (exact) return exact;
  return pricing.find((c) => hintMatchesCity(n, c)) || null;
}

/** Centros aproximados para resolver ciudad por GPS cuando el nombre municipal no coincide. */
const CITY_HUB_COORDS = {
  medellin: { lat: 6.2476, lng: -75.5658 },
  barranquilla: { lat: 10.9685, lng: -74.7813 },
};

function haversineKm(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Ciudad con tarifas más cercana a lat/lng (hubs conocidos + city.lat/lng si existen).
 * Radio máximo ~180 km para no mezclar Medellín con Barranquilla.
 */
export function matchPricingCityByCoords(cities, lat, lng) {
  const pricing = getPricingCities(cities);
  const la = Number(lat);
  const ln = Number(lng);
  if (!pricing.length || !Number.isFinite(la) || !Number.isFinite(ln)) return null;

  let best = null;
  let bestKm = Infinity;
  pricing.forEach((city) => {
    const key = normalizeCityName(city.name);
    const hub =
      (Number.isFinite(Number(city.lat)) && Number.isFinite(Number(city.lng))
        ? { lat: Number(city.lat), lng: Number(city.lng) }
        : null) ||
      (Number.isFinite(Number(city.latitude)) && Number.isFinite(Number(city.longitude))
        ? { lat: Number(city.latitude), lng: Number(city.longitude) }
        : null) ||
      CITY_HUB_COORDS[key] ||
      null;
    if (!hub) return;
    const km = haversineKm(la, ln, hub.lat, hub.lng);
    if (km < bestKm) {
      bestKm = km;
      best = city;
    }
  });
  return best && bestKm <= 180 ? best : null;
}

/**
 * Ciudad efectiva del viaje para tarifas.
 * Prioridad: cityId explícito del viaje → única ciudad con tarifas → perfil.
 */
export function resolveTripCityId(cities, explicitCityId, profileCityId) {
  const pricing = getPricingCities(cities);
  if (explicitCityId && pricing.some((c) => c.id === explicitCityId)) {
    return explicitCityId;
  }
  if (pricing.length === 1) {
    return pricing[0].id;
  }
  if (profileCityId && pricing.some((c) => c.id === profileCityId)) {
    return profileCityId;
  }
  return explicitCityId || profileCityId || null;
}
